import globalUtils from './globalUtils';

// Visual grouping only: no normalization, digit calculation or persisted-value mutation.
export function formatBankSlipLine(value) {
  if (typeof value !== 'string' || !/^\d{47}$/.test(value)) return value;
  return value.replace(/^(\d{5})(\d{5})(\d{5})(\d{6})(\d{5})(\d{6})(\d)(\d{14})$/, '$1.$2 $3.$4 $5.$6 $7 $8');
}

// Presentation identity only. Never used to build a banking request.
const providerIdentities = Object.freeze({
  SICREDI: { name: 'SICREDI', code: '748-X' }
});
const present = value => value !== null && value !== undefined && value !== '';
const join = values => values.filter(present).join(' · ');

export function bankSlipDocument(cobranca = {}, boleto = {}, identity = {}, formatMoney, formatDate) {
  const beneficiary = cobranca.beneficiario_documento;
  const beneficiaryDocument = globalUtils.formatarDocumento(beneficiary?.documento);
  const beneficiaryAddress = beneficiary?.endereco || {};
  if (beneficiary?.bancaria) cobranca = { ...cobranca, ...beneficiary.bancaria };
  const provider = providerIdentities[cobranca.provedor];
  const payer = cobranca.pagador_documento || {};
  const address = payer.endereco || {};
  const field = (label, value) => present(value) ? { label, value: String(value) } : null;
  const dated = value => present(value) ? formatDate(value) : null;
  const instructions = [];
  for (const kind of ['multa', 'juros', 'desconto']) {
    const type = boleto[`${kind}_tipo`];
    const value = boleto[`${kind}_valor`];
    const date = boleto[`${kind}_${kind === 'desconto' ? 'data_limite' : 'data_inicio'}`];
    if (boleto.sicredi_conferencia_json?.versao === 1 && date == null && present(value) && Number(value) > 0 &&
        ((kind === 'juros' && type === 'PERCENTUAL_DIA_SEM_DATA') || (kind === 'multa' && type === 'PERCENTUAL'))) {
      const amount = Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
      instructions.push(kind === 'juros' ? `Mora solicitada: ${amount}% ao dia.` : `Multa solicitada: ${amount}% (única).`);
      continue;
    }
    if (kind === 'juros' && type === 'VALOR_DIA_SEM_DATA' && cobranca.provedor === 'SICREDI' && present(value) && Number(value) > 0 && date == null) {
      instructions.push(`Juros: ${formatMoney(value)} por dia.`);
      continue;
    }
    const units = kind === 'juros' ? { VALOR_DIA: 'por dia', PERCENTUAL_MES: '% ao mês' }
      : { VALOR: '', PERCENTUAL: '%' };
    if (!Object.prototype.hasOwnProperty.call(units, type) || !present(value) || !Number.isFinite(Number(value)) || Number(value) <= 0 || !date) continue;
    const amount = type.startsWith('PERCENTUAL') ? (kind === 'desconto' ? String(value) : Number(value).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 4 })) : formatMoney(value);
    instructions.push(`${{ multa: 'Multa', juros: 'Juros', desconto: 'Desconto' }[kind]}: ${amount}${units[type].startsWith('%') || (kind === 'multa' && !units[type]) ? '' : ' '}${units[type]} ${kind === 'desconto' ? 'até' : 'a partir de'} ${formatDate(date)}.`);
  }
  return {
    school: {
      name: identity.primaryName || identity.beneficiaryName || '',
      details: [identity.legalName, identity.cnpj ? `CNPJ ${globalUtils.formatarDocumento(identity.cnpj)}` : '',
        ...(identity.addressLines || []), (identity.contacts || []).join(' · ')].filter(present)
    },
    bankName: provider?.name || cobranca.provedor || '',
    bankCode: provider?.code || cobranca.codigo_banco || '',
    // Instruction rates are not assessed charges. Only the nominal amount and
    // an explicitly monetary discount are available in the current contract.
    financialFields: [
      { label: 'Vencimento', value: dated(cobranca.data_vencimento_atual) || '', kind: 'due' },
      { label: '(=) Valor do Documento', value: present(cobranca.valor_atual) ? formatMoney(cobranca.valor_atual) : '', kind: 'amount' },
      { label: '(-) Desconto / Abatimento', value: boleto.desconto_tipo === 'VALOR' && present(boleto.desconto_valor) && Number.isFinite(Number(boleto.desconto_valor)) && Number(boleto.desconto_valor) > 0 ? formatMoney(boleto.desconto_valor) : '', kind: 'discount' },
      { label: '(-) Outras Deduções', value: '', kind: 'deductions' },
      { label: '(+) Mora / Multa', value: '', kind: 'fees' },
      { label: '(+) Outros Acréscimos', value: '', kind: 'additions' },
      { label: '(=) Valor Cobrado', value: '', kind: 'charged' }
    ],
    rows: [
      [field('Beneficiário', beneficiary?.nome), field(beneficiary?.documento?.length === 11 ? 'CPF do beneficiário' : 'CNPJ do beneficiário', beneficiaryDocument)],
      [field('Endereço do beneficiário', join([beneficiaryAddress.logradouro, beneficiaryAddress.numero, beneficiaryAddress.complemento, beneficiaryAddress.bairro, join([beneficiaryAddress.municipio, beneficiaryAddress.uf]), globalUtils.formatarCEP(beneficiaryAddress.cep)]))],
      [field(cobranca.codigo_agencia && cobranca.codigo_beneficiario ? 'Agência / Código do Beneficiário' : cobranca.codigo_agencia ? 'Agência / Cooperativa' : 'Código do Beneficiário', [cobranca.codigo_agencia, cobranca.codigo_beneficiario].filter(present).join(' / ')), field('Posto', cobranca.codigo_posto)],
      [field('Data do Documento', dated(cobranca.data_emissao)), field('Nº do Documento / Seu Número', boleto.seu_numero), field('Data de Processamento', dated(cobranca.data_registro)), field('Nosso Número', boleto.nosso_numero)],
      [field('Instruções', instructions.join('\n'))],
      [field('Pagador', payer.nome), field('CPF/CNPJ do pagador', globalUtils.formatarDocumento(payer.documento))],
      [field('Endereço do pagador', join([join([address.logradouro, address.numero, address.complemento, address.bairro]), join([address.municipio, address.uf]), globalUtils.formatarCEP(address.cep)]))]
    ].map(row => row.filter(Boolean)).filter(row => row.length),
    line: boleto.linha_digitavel || '', displayLine: formatBankSlipLine(boleto.linha_digitavel || ''), barcode: boleto.codigo_barras || '',
    pix: boleto.pix_copia_cola || '', txid: boleto.pix_txid || '',
    warning: cobranca.status === 'CANCELADA' ? 'COBRANÇA CANCELADA — NÃO PAGAR'
      : cobranca.provedor === 'SICREDI' && !beneficiary ? 'IDENTIDADE DOCUMENTAL NÃO COMPROVADA — DOCUMENTO INDISPONÍVEL'
        : cobranca.ambiente === 'HOMOLOGACAO' ? 'TESTE — NÃO PAGAR'
      : [cobranca.ambiente, cobranca.provedor, cobranca.adaptador].includes('MOCK') ? 'AMBIENTE MOCK — DOCUMENTO SEM VALOR BANCÁRIO' : ''
  };
}

// Shared by the screen and the isolated print window; app chrome is never copied.
export const bankSlipPrintCss = `
.bank-slip { width: 100%; max-width: 186mm; margin: auto; background: white; color: #111; font-family: Arial, sans-serif; font-size: 11px; padding: 0; }
.bank-slip * { box-sizing: border-box; }
.bank-slip h2 { font-size: 12px; margin: 0 0 8px; font-weight: 700; }
.bank-slip p { margin: 3px 0; }
.bank-slip .bank-school { display: flex; align-items: center; gap: 5mm; border-bottom: 1px solid #333; padding-bottom: 3mm; margin-bottom: 3mm; break-inside: avoid; }
.bank-slip .bank-school > div { min-width: 0; overflow-wrap: anywhere; }
.bank-slip .bank-school h2 { font-size: 15px; margin-bottom: 4px; }
.bank-slip .bank-school p { font-size: 10px; }
.bank-slip .bank-school img { max-width: 26mm; max-height: 20mm; object-fit: contain; }
.bank-slip .bank-pix { display: flex; gap: 16px; padding: 10px 0 16px; break-inside: avoid; }
.bank-slip .bank-pix img { width: 34mm; height: 34mm; object-fit: contain; }
.bank-slip .bank-pix-content { min-width: 0; flex: 1; }
.bank-slip code { color: #111; white-space: pre-wrap; overflow-wrap: anywhere; font-size: 10px; }
.bank-slip .bank-pix code { display: block; margin: 8px 0; }
.bank-slip .bank-header { display: flex; align-items: center; gap: 24px; border-bottom: 2px solid #111; padding: 8px 0; }
.bank-slip .bank-identity { display: flex; align-items: center; gap: 10px; flex-shrink: 0; }
.bank-slip .bank-name { font-size: 17px; font-weight: bold; }
.bank-slip .bank-code { border-left: 1px solid; border-right: 1px solid; padding: 0 10px; font-size: 17px; white-space: nowrap; }
.bank-slip .bank-line { flex: 1; min-width: 0; text-align: right; font-family: "Courier New", monospace; font-weight: bold; font-size: 12px; line-height: 1.5; overflow-wrap: anywhere; }
.bank-slip .bank-section { break-inside: avoid; }
.bank-slip .bank-details { display: flex; border-bottom: 1px solid #333; }
.bank-slip .bank-main-fields { flex: 1; min-width: 0; }
.bank-slip .bank-main-fields .bank-row:last-child { border-bottom: 0; }
.bank-slip .bank-financial { flex: 0 0 46mm; border-left: 1px solid #333; display: flex; flex-direction: column; }
.bank-slip .bank-financial-field { text-align: right; flex: 1; padding: 3px 10px; border-bottom: 1px solid #333; min-width: 0; }
.bank-slip .bank-financial-field:last-child { border-bottom: 0; }
.bank-slip .bank-financial .bank-label { font-size: 9px; text-transform: uppercase; margin-bottom: 3px; }
.bank-slip .bank-financial .bank-value { min-height: 6px; font-size: 11px; font-weight: 700; line-height: 1.25; }
.bank-slip .bank-financial .bank-due { font-size: 18px; }
.bank-slip .bank-financial .bank-amount { font-size: 22px; }
.bank-slip .bank-row { display: flex; border-bottom: 1px solid #333; }
.bank-slip .bank-field { flex: 1; min-width: 0; padding: 5px 7px; border-right: 1px solid #333; }
.bank-slip .bank-field:first-child { padding-left: 0; }
.bank-slip .bank-field:last-child { border-right: 0; }
.bank-slip .bank-label { display: block; font-size: 9px; margin-bottom: 3px; }
.bank-slip .bank-value { display: block; white-space: pre-wrap; overflow-wrap: anywhere; font-weight: 600; }
.bank-slip .bank-cut { border: 0; border-top: 1px dashed #555; margin: 18px 0; }
.bank-slip .bank-barcode { margin-top: 14px; break-inside: avoid; }
.bank-slip .bank-barcode img { display: block; width: 103mm; height: 13mm; object-fit: fill; max-width: 100%; }
.bank-slip .bank-barcode code { display: block; margin-top: 4px; }
.bank-slip .bank-footer { text-align: right; margin-top: 6px; font-weight: bold; }
.bank-slip .bank-warning { border: 2px solid; padding: 8px; margin-bottom: 10px; font-weight: bold; }
@media screen and (max-width: 575px) {
  .bank-slip .bank-header { flex-wrap: wrap; gap: 8px; }
  .bank-slip .bank-line { flex-basis: 100%; text-align: left; }
  .bank-slip .bank-details { flex-direction: column; }
  .bank-slip .bank-financial { order: -1; flex: initial; display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); border-left: 0; }
  .bank-slip .bank-financial-field:nth-child(odd) { border-right: 1px solid #333; }
  .bank-slip .bank-row { flex-wrap: wrap; }
  .bank-slip .bank-field { flex: 1 1 50%; }
}
@page { size: A4; margin: 12mm; }
@media print { .no-print { display: none !important; } .bank-slip { max-width: none; } }
`;
